import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  Timestamp,
  setDoc,
  doc,
  deleteDoc,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
// If you have a logo, keep this import (optional)
// import logoUrl from "../components/assets/logo.png";

const PAGE_SIZE = 25;
const LOGO_W_PT = 64;
const PDF_MAX_IMAGES = 12;
const PDF_IMG_COLS = 3;
const PDF_IMG_SIZE = 150;
const PDF_IMG_GAP = 10;
const PDF_LEFT_MARGIN = 40;

const VIEW = {
  CYL: "cylinders",
  PARTS: "parts",
};

export default function ReturnsArchive() {
  const [view, setView] = useState(VIEW.CYL);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState("");

  // filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [qtext, setQtext] = useState("");

  // pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [lastSnap, setLastSnap] = useState(null);

  // Optional logo preload for PDF header
  const [logoImg, setLogoImg] = useState(null);
  // If you have a logo file, uncomment this block and the import above
  /*
  useEffect(() => {
    if (!logoUrl) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = logoUrl;
    img.onload = () => setLogoImg(img);
  }, []);
  */

  const baseCol =
    view === VIEW.CYL ? "returns_archive" : "returns_parts_archive";

  const fetchPage = async ({ reset } = { reset: true }) => {
    setLoading(true);
    setError("");

    try {
      const col = collection(db, baseCol);
      const parts = [orderBy("createdAt", "desc")];

      if (fromDate) {
        parts.push(
          where(
            "createdAt",
            ">=",
            Timestamp.fromDate(new Date(fromDate + "T00:00:00"))
          )
        );
      }
      if (toDate) {
        parts.push(
          where(
            "createdAt",
            "<=",
            Timestamp.fromDate(new Date(toDate + "T23:59:59"))
          )
        );
      }
      if (!reset && lastSnap) parts.push(startAfter(lastSnap));
      parts.push(limit(PAGE_SIZE));

      const qy = query(col, ...parts);
      const snap = await getDocs(qy);
      const docs = snap.docs.map((d) => ({ _snap: d, ...d.data(), id: d.id }));
      setRows(docs);
      setLastSnap(snap.docs[snap.docs.length - 1] || null);
      setPageIndex((i) => (reset ? 0 : i + 1));
    } catch (e) {
      console.error(e);
      setError(
        "Failed to load archived returns. If you applied date filters, Firestore may need a composite index."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLastSnap(null);
    fetchPage({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const applyFilters = async () => {
    setLastSnap(null);
    await fetchPage({ reset: true });
  };

  const nextPage = async () => {
    if (rows.length === 0 || !lastSnap) return;
    await fetchPage({ reset: false });
  };

  const prevPage = async () => {
    setLastSnap(null);
    await fetchPage({ reset: true });
  };

  const qtextLower = qtext.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!qtextLower) return rows;
    return rows.filter((r) => {
      const f = r.payload?.form || {};
      const hay =
        [
          f.serialNumber,
          f.sretNumber,
          f.returnsNoteNumber,
          f.cylinderModel,
          f.reasonCode,
          f.reportedCode,
          f.faultFoundCode,
          r.id,
          f.partCode,
          f.partTitle,
        ]
          .filter(Boolean)
          .map((x) => String(x).toLowerCase()) || [];
      return hay.some((x) => x.includes(qtextLower));
    });
  }, [rows, qtextLower]);

  /* -------------------- Unarchive -------------------- */
  const unarchive = async (r) => {
    const targetCol = view === VIEW.CYL ? "returns" : "returns_parts";
    if (
      !window.confirm(
        "Unarchive this report? It will be moved back to the active list."
      )
    )
      return;

    try {
      const copy = { ...r };
      delete copy._snap;

      // Keep status as-is (likely "complete"); stamp unarchivedAt
      const moved = {
        ...copy,
        unarchivedAt: serverTimestamp(),
        statusUpdatedAt: serverTimestamp(),
      };

      // Create a new doc in active collection
      await setDoc(doc(collection(db, targetCol)), moved);

      // Delete from archive using the actual snapshot ref
      if (r._snap?.ref) {
        await deleteDoc(r._snap.ref);
      } else {
        // fallback if _snap somehow missing
        await deleteDoc(doc(db, baseCol, r.id));
      }

      // Update UI
      setRows((prev) => prev.filter((x) => x.id !== r.id));
    } catch (e) {
      console.error(e);
      alert("Could not unarchive this report.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* header + filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold">Archived Returns</h1>

          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              className={`px-3 py-1.5 border text-sm rounded-l-md ${
                view === VIEW.CYL
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white border-gray-300 hover:bg-gray-50"
              }`}
              onClick={() => setView(VIEW.CYL)}
            >
              Cylinders
            </button>
            <button
              className={`px-3 py-1.5 border text-sm rounded-r-md ${
                view === VIEW.PARTS
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white border-gray-300 hover:bg-gray-50"
              }`}
              onClick={() => setView(VIEW.PARTS)}
            >
              Parts
            </button>
          </div>
        </div>

        {/* filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:items-center gap-2 sm:ml-auto">
          <div className="flex items-center gap-2">
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm w-full"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              title="From date"
            />
            <span className="hidden sm:inline text-gray-500 text-sm">to</span>
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm w-full"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              title="To date"
            />
          </div>

          <button
            className="px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
            onClick={applyFilters}
            disabled={loading}
          >
            Apply
          </button>

          <input
            className="border rounded px-2 py-1 text-sm w-full sm:w-64"
            placeholder="Search serial / SRET / note / model / code / id"
            value={qtext}
            onChange={(e) => setQtext(e.target.value)}
          />
        </div>
      </div>

      {error && <div className="mb-3 text-red-600 text-sm">{error}</div>}

      {/* list */}
      <div className="space-y-3">
        {loading && rows.length === 0 && (
          <div className="text-gray-500">Loading…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="text-gray-500">No archived returns found.</div>
        )}

        {filtered.map((r) => {
          const f = r.payload?.form || {};
          const dt = r.createdAt?.toDate?.();
          return (
            <div key={r.id} className="border rounded-lg bg-white">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 p-3">
                <div className="text-[11px] sm:text-xs rounded px-2 py-0.5 border bg-gray-50">
                  {view === VIEW.CYL ? "Cylinder" : "Part"}
                </div>

                <div className="font-medium text-sm sm:text-base truncate max-w-[50%] sm:max-w-none">
                  {view === VIEW.CYL
                    ? f.cylinderModel || "—"
                    : f.partCode || f.partTitle || "—"}
                </div>

                <div className="text-xs sm:text-sm text-gray-600">
                  Serial: <span className="font-mono">{f.serialNumber || "—"}</span>
                </div>

                <div className="text-xs sm:text-sm text-gray-600">
                  SRET #: <span className="font-mono">{f.sretNumber || "—"}</span>
                </div>

                <div className="text-[11px] sm:text-xs rounded px-2 py-0.5 border bg-gray-50">
                  Status: {r.status || "—"}
                </div>

                <div className="ml-auto text-xs sm:text-sm text-gray-500">
                  {dt ? dt.toLocaleString() : "—"}
                </div>

                <div className="w-full sm:w-auto flex flex-wrap gap-2 mt-2 sm:mt-0">
                  <button
                    className="text-indigo-600 hover:underline text-sm"
                    onClick={() =>
                      setOpenId((p) => (p === r.id ? null : r.id))
                    }
                  >
                    {openId === r.id ? "Hide" : "View"}
                  </button>

                  <button
                    className="px-2 py-1 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
                    onClick={async () => {
                      if (view === VIEW.CYL) await pdfOneCyl(r, logoImg);
                      else await pdfOneParts(r, logoImg);
                    }}
                    title="Generate PDF"
                  >
                    PDF
                  </button>

                  <button
                    className="px-2 py-1 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
                    onClick={() => unarchive(r)}
                    title="Move back to active list"
                    disabled={loading}
                  >
                    Unarchive
                  </button>
                </div>
              </div>

              {openId === r.id && (
                <div className="p-3 sm:p-4 bg-gray-50 border-t text-[13px] sm:text-sm leading-relaxed overflow-x-auto">
                  <ReportPanel r={r} view={view} />
                  {/* No AdminBox, no edit controls in archive */}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* pagination */}
      <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <button
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
          disabled={loading || pageIndex === 0}
          onClick={prevPage}
        >
          Prev
        </button>
        <span className="text-sm text-gray-600 text-center sm:text-left">
          Page {pageIndex + 1}
        </span>
        <button
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
          disabled={loading || rows.length < PAGE_SIZE}
          onClick={nextPage}
        >
          Next
        </button>
      </div>
    </div>
  );
}

/* -------------------- Report panel (read-only) -------------------- */

function KV({ label, value, mono }) {
  return (
    <div className="flex gap-2">
      <span className="w-48 text-gray-600">{label}</span>
      <span className={mono ? "font-mono" : ""}>{value || "—"}</span>
    </div>
  );
}

function ReportPanel({ r, view }) {
  const f = r.payload?.form || {};
  const p = r.payload?.parts || {};
  const c = r.payload?.cylCheck || {};

  if (view === "parts") {
    const t = r.payload?.templateSnapshot || {};
    return (
      <div className="space-y-3">
        <h3 className="font-semibold">Return & Part</h3>
        <KV label="Returns Note #" value={f.returnsNoteNumber} />
        <KV label="Serial" value={f.serialNumber} mono />
        <KV label="SRET #" value={f.sretNumber} mono />
        <KV label="Part Code" value={f.partCode} />
        <KV label="Part Title" value={f.partTitle || t.title} />
        <KV label="Part Description" value={f.partDescription || t.description} />
        <h3 className="font-semibold mt-4">Reported / Fault Found</h3>
        <KV label="Reported" value={f.reportedCode} />
        <KV label="Fault Found" value={f.faultFoundCode} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="font-semibold">Return & Cylinder</h3>
      <KV label="Returns Note #" value={f.returnsNoteNumber} />
      <KV label="Serial" value={f.serialNumber} mono />
      <KV label="SRET #" value={f.sretNumber} mono />
      <KV label="Model" value={f.cylinderModel} />
      <KV label="Capacity" value={f.capacity} />
      <KV label="Manufacture Date" value={f.dateOfManufacture} />
      <h3 className="font-semibold mt-4">Reasons & Fault</h3>
      <KV label="Return Reason" value={f.reasonCode} />
      <KV label="Reason Defect" value={f.reasonDefectCode} />
      <KV label="Fault Found" value={f.faultFoundCode} />
      <KV label="Fault Defect" value={f.faultDefectCode} />
      <h3 className="font-semibold mt-4">Cylinder Checklist</h3>
      <KV label="Installed" value={bool(c.cylinderInstalled)} />
      <KV label="Damage: Case" value={bool(c.damageToCase)} />
      <KV label="Damage: Packaging" value={bool(c.damageToPackaging)} />
      <KV label="Damage: Bosses" value={bool(c.damageToBosses)} />
      <KV label="Strip & Test" value={bool(c.stripAndTest)} />
      <h3 className="font-semibold mt-4">Parts Mode</h3>
      <KV label="Mode" value={partsMode(r.payload?.parts)} />
    </div>
  );
}

/* -------------------- PDF helpers -------------------- */

function bool(v) {
  return v ? "Yes" : "No";
}

function partsMode(p = {}) {
  if (p?.noPartsReturned) return "No parts returned";
  if (p?.allPartsReturned) return "All parts returned";
  if (p?.partialReturn) {
    const items = Object.entries(p.partialReturnItems || {})
      .filter(([, v]) => !!v)
      .map(([k]) => k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()));
    return items.length ? `Partial: ${items.join(", ")}` : "Partial: none selected";
  }
  return "—";
}

function pdfFileName(r, prefix = "return") {
  const f = r.payload?.form || {};
  const dt = r.createdAt?.toDate?.();
  const date = dt ? dt.toISOString().slice(0, 10) : "unknown";
  const serial = f.serialNumber
    ? String(f.serialNumber).replace(/[^A-Za-z0-9_\-]/g, "")
    : "noserial";
  return `${prefix}_${serial}_${date}_${r.id.slice(-6)}.pdf`;
}

function drawTable(doc, rows, x, y) {
  const body = rows.map((r) => r.map((c) => (c == null ? "—" : String(c))));
  autoTable(doc, {
    body,
    startY: y,
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 4, halign: "left", valign: "middle" },
    headStyles: { fillColor: [240, 240, 240] },
    margin: { left: x, right: 40 },
  });
}

/* ----- Optional SharePoint image fetch (same as your live list) ----- */

async function fetchDraftFolderAndImages(r) {
  const draftId = r.draftRef;
  if (!draftId) return { folderId: null, folderUrl: null, images: [] };

  const draftCol = r.payload?.form?.partCode
    ? "returns_parts_draft"
    : "returns_draft";

  const snap = await getDoc(doc(db, draftCol, draftId));
  if (!snap.exists()) return { folderId: null, folderUrl: null, images: [] };

  const storage = snap.data()?.storage || {};
  const folderId = storage.folderId || null;
  const folderUrl = storage.folderUrl || null;
  if (!folderId) return { folderId: null, folderUrl: folderUrl || null, images: [] };

  const fns = getFunctions(undefined, "europe-west2");
  const list = httpsCallable(fns, "spListImages");
  const { data } = await list({ folderItemId: folderId });
  return { folderId, folderUrl, images: data?.images || [] };
}


async function getImageBase64(itemId) {
  const fns = getFunctions(undefined, "europe-west2");
  const getData = httpsCallable(fns, "spGetImageData");
  const { data } = await getData({ itemId });
  return data; // { mime, base64 }
}

/* -------------------- PDF: Cylinders -------------------- */

async function pdfOneCyl(r, logoImg) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const f = r.payload?.form || {};
  const p = r.payload?.parts || {};
  const c = r.payload?.cylCheck || {};
  const dt = r.createdAt?.toDate?.();
  const pageW = doc.internal.pageSize.getWidth();

  if (logoImg) {
    const naturalW = logoImg.naturalWidth || 64;
    const naturalH = logoImg.naturalHeight || 64;
    const w = LOGO_W_PT;
    const h = (naturalH / naturalW) * w;
    doc.addImage(logoImg, "PNG", pageW - 40 - w, 24, w, h);
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Cylinder Return Report (Archived)", 40, 50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  if (dt) doc.text(`Created: ${dt.toLocaleDateString()}`, 40, 68);
  if (r.status) doc.text(`Status: ${String(r.status)}`, 40, 82);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Return & Cylinder", 40, 110);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  drawTable(
    doc,
    [
      ["Returns Note #", f.returnsNoteNumber || "—", "Serial", f.serialNumber || "—"],
      ["SRET #", f.sretNumber || "—", "Model", f.cylinderModel || "—"],
      ["Capacity", f.capacity || "—", "Manufacture Date", f.dateOfManufacture || "—"],
    ],
    40,
    120
  );

  let y = (doc.lastAutoTable?.finalY ?? 200) + 20;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Reasons & Fault Found", 40, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  drawTable(
    doc,
    [
      ["Return Reason", f.reasonCode || "—", "Reason Defect", f.reasonDefectCode || "—"],
      ["Fault Found", f.faultFoundCode || "—", "Fault Defect", f.faultDefectCode || "—"],
    ],
    40,
    y + 10
  );

  y = (doc.lastAutoTable?.finalY ?? y) + 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Cylinder Checklist", 40, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  drawTable(
    doc,
    [
      ["Installed", bool(c.cylinderInstalled), "Damage to Case", bool(c.damageToCase)],
      ["Damage to Packaging", bool(c.damageToPackaging), "Damage to Bosses", bool(c.damageToBosses)],
      ["Strip & Test", bool(c.stripAndTest), "", ""],
    ],
    40,
    y + 10
  );

  // Images (optional; same mechanics as live list)
  const { images } = await fetchDraftFolderAndImages(r);
  if (images.length) {
    y = (doc.lastAutoTable?.finalY ?? y) + 30;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Photos", 40, y);

    let x = PDF_LEFT_MARGIN;
    y += 10;

    const take = images.slice(0, PDF_MAX_IMAGES);
    for (let i = 0; i < take.length; i++) {
      const info = await getImageBase64(take[i].id);
      if (!info?.base64 || !info?.mime) continue;
      const col = i % PDF_IMG_COLS;
      const row = Math.floor(i / PDF_IMG_COLS);
      const px = x + col * (PDF_IMG_SIZE + PDF_IMG_GAP);
      const py = y + row * (PDF_IMG_SIZE + PDF_IMG_GAP);
      if (row && py + PDF_IMG_SIZE > doc.internal.pageSize.getHeight() - 40) {
        doc.addPage();
        y = 40;
      }
      doc.addImage(`data:${info.mime};base64,${info.base64}`, "JPEG", px, py, PDF_IMG_SIZE, PDF_IMG_SIZE);
    }
  }

  doc.save(pdfFileName(r, "return_archived"));
}

/* -------------------- PDF: Parts -------------------- */

async function pdfOneParts(r, logoImg) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const f = r.payload?.form || {};
  const t = r.payload?.templateSnapshot || {};
  const dt = r.createdAt?.toDate?.();
  const pageW = doc.internal.pageSize.getWidth();

  if (logoImg) {
    const naturalW = logoImg?.naturalWidth || 64;
    const naturalH = logoImg?.naturalHeight || 64;
    const w = LOGO_W_PT;
    const h = (naturalH / naturalW) * w;
    doc.addImage(logoImg, "PNG", pageW - 40 - w, 24, w, h);
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Parts Return Report (Archived)", 40, 50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  if (dt) doc.text(`Created: ${dt.toLocaleDateString()}`, 40, 68);
  if (r.status) doc.text(`Status: ${String(r.status)}`, 40, 82);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Return & Part", 40, 110);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  drawTable(
    doc,
    [
      ["Returns Note #", f.returnsNoteNumber || "—", "Serial", f.serialNumber || "—"],
      ["SRET #", f.sretNumber || "—", "Part Code", f.partCode || "—"],
      ["Part Title", f.partTitle || t.title || "—", "Part Desc", (f.partDescription || t.description || "—").slice(0, 48)],
    ],
    40,
    120
  );

  let y = (doc.lastAutoTable?.finalY ?? 200) + 20;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Reported / Fault Found", 40, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  drawTable(
    doc,
    [
      ["Reported", f.reportedCode || "—", "Fault Found", f.faultFoundCode || "—"],
    ],
    40,
    y + 10
  );

  doc.save(pdfFileName(r, "return_part_archived"));
}
